<template>
    <div
        class="ticket-type-add-to-basket border border-solid border-gray-300 bg-white rounded-xl"
        :class="{
            'sold-out': ticketType.available < 1,
            'enquiry-enabled':
                ticketType.enquiry_enabled == 1 || (ticketType.enquiry_enabled == 2 && ticketType.available < 1)
        }"
    >
        <slot name="purchase-block-header-image"></slot>
        <div class="px-6 pt-2 font-bold">
            {{ ticketType.type }}
        </div>
        <slot name="purchase-block-header"></slot>
        <div class="px-6 text-gray-500 flex items-center" v-if="ticketType.ticket_format">
            <div class="pr-1 pt-1">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M2.84496 0.757754C1.91646 0.757754 1.16376 1.51045 1.16376 2.43896V9.50001C1.16376 10.4285 1.91646 11.1812 2.84496 11.1812H7.21609C8.14459 11.1812 8.89729 10.4285 8.89729 9.50001V2.43896C8.89729 1.51045 8.14459 0.757754 7.21609 0.757754H2.84496Z"
                        stroke="#858585"
                        stroke-width="0.672481"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M5.19952 9.16382H4.86328"
                        stroke="#858585"
                        stroke-width="0.672481"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                    <path
                        d="M10.9141 7.48271C10.9141 7.48271 11.2503 6.87327 11.2503 5.96963C11.2503 5.06598 10.9141 4.45654 10.9141 4.45654"
                        stroke="#858585"
                        stroke-width="0.672481"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </div>
            <div class="flex-1">
                {{ ticketType.ticket_format }}
            </div>
        </div>
        <div class="px-6 pb-6 pt-4">
            <template
                v-if="ticketType.enquiry_enabled != 1 && (ticketType.enquiry_enabled != 2 || ticketType.available >= 1)"
            >
                <h3 class="mt-0">Quantity</h3>
                <div class="quantity-control flex border border-solid border-gray-300 bg-white rounded-xl">
                    <div
                        @click="onClickQuantityDown"
                        class="quantity-down py-2 px-4"
                        :class="{ enabled: selectedQuantity > minQuantity }"
                    >
                        -
                    </div>
                    <div class="flex-1 text-center py-2">{{ selectedQuantity }}</div>
                    <div
                        @click="onClickQuantityUp"
                        class="quantity-up py-2 px-4"
                        :class="{ enabled: selectedQuantity < maxQuantity }"
                    >
                        +
                    </div>
                </div>
                <hr class="mt-4 mb-3" />
                <div class="price-info flex flex-col mt-2 mb-4">
                    <div class="flex mb-2">
                        <div class="flex-1">Delivery Cost</div>
                        <div class="" v-html="currency.symbol + deliveryPrice.toFixed(2)"></div>
                    </div>
                    <div class="flex mb-2">
                        <div class="flex-1">Total Quantity</div>
                        <div class="">x{{ selectedQuantity }}</div>
                    </div>
                    <div class="flex mb-2">
                        <div class="flex-1">Cost</div>
                        <div class="" v-html="currency.symbol + totalTicketCost.toFixed(2)"></div>
                    </div>
                    <div class="flex">
                        <div class="flex-1">Booking Fee</div>
                        <div class="" v-html="currency.symbol + totalBookingFee.toFixed(2)"></div>
                    </div>
                    <hr class="my-3" />
                    <div class="flex">
                        <div class="flex-1">Subtotal</div>
                        <div class="" v-html="currency.symbol + subtotal.toFixed(2)"></div>
                    </div>
                </div>
                <slot name="purchase-block-footer"></slot>
                <button
                    :disabled="ticketType.available < 1"
                    @click="onClickBuyTickets"
                    class="book-tickets-button mt-3 bg-blue-600 hover:bg-blue-800 text-white py-2 px-6 w-full rounded-md font-medium"
                >
                    <template v-if="ticketType.available > 0">
                        Book Tickets
                    </template>
                    <template v-else>
                        Sold Out
                    </template>
                </button>
            </template>
            <button
                v-if="ticketType.enquiry_enabled == 1 || (ticketType.enquiry_enabled == 2 && ticketType.available < 1)"
                @click="onClickShowEnquiryModal"
                class="enquire-button mt-3 bg-blue-600 hover:bg-blue-800 text-white py-2 px-6 w-full rounded-md font-medium"
            >
                Enquire Now
            </button>
        </div>
        <TicketEnquiryModal
            v-if="showEnquiryModal"
            v-on:close="showEnquiryModal = false"
            :event-schedule-id="this.scheduleId"
            :ticket-type="ticketType.type"
        ></TicketEnquiryModal>
    </div>
</template>

<script>
import TicketEnquiryModal from "./ticket-enquiry-modal.vue";

export default {
    components: {
        TicketEnquiryModal
    },
    props: {
        ticketType: {
            type: Object,
            required: true
        },
        scheduleId: {
            type: Number,
            required: true
        }
    },
    data: function() {
        return {
            website: window.website,
            currency: window.currency,
            deliveryPrice: parseFloat(window.website.ecommerce.delivery_charge),
            loading: true,
            selectedQuantity: Math.max(1, this.ticketType.min_quantity),
            showEnquiryModal: false
        };
    },
    computed: {
        totalTicketCost: function() {
            return (this.selectedQuantity * this.ticketType.price * 100) / 100;
        },
        totalBookingFee: function() {
            if (this.ticketType.booking_fee_basis == 0) {
                return parseFloat(this.ticketType.booking_fee / 100) * this.totalTicketCost;
            } else if (this.ticketType.booking_fee_basis == 1) {
                return this.ticketType.booking_fee * this.selectedQuantity;
            }
            return 0;
        },
        subtotal: function() {
            return this.totalTicketCost + this.deliveryPrice + parseFloat(this.ticketType.booking_fee);
        },
        minQuantity: function() {
            return Math.max(1, this.ticketType.min_quantity);
        },
        maxQuantity: function() {
            if (!this.ticketType.max_quantity) this.ticketType.max_quantity = 99;
            return Math.min(this.ticketType.available, this.ticketType.max_quantity);
        }
    },
    mounted() {},
    methods: {
        setQuantity(newQuantity) {
            if (newQuantity < this.minQuantity) newQuantity = this.minQuantity;
            if (newQuantity > this.maxQuantity) newQuantity = this.maxQuantity;
            this.selectedQuantity = newQuantity;
        },
        onClickQuantityDown() {
            this.setQuantity(this.selectedQuantity - 1);
        },
        onClickQuantityUp() {
            this.setQuantity(this.selectedQuantity + 1);
        },
        async onClickBuyTickets() {
            var ticket = _.cloneDeep(this.ticketType);
            ticket.qty = this.selectedQuantity;
            let response = await axios.post("/basket/add", {
                schedule_id: this.scheduleId,
                tickets: ticket
            });
            if (response.status === 200) {
                this.$toasted.show("The ticket has been added to your basket.", {
                    theme: "bubble",
                    type: "success",
                    position: "top-center",
                    duration: 3000
                });
                window.location = window.website.base_url + "/booking";
            } else {
                this.$toasted.show("There was a problem adding your tickets.", {
                    theme: "bubble",
                    type: "error",
                    position: "top-center",
                    duration: 3000
                });
            }
        },
        onClickShowEnquiryModal() {
            this.showEnquiryModal = true;
        }
    }
};
</script>

<style lang="scss" scoped>
@import "../../sass/base/_variables.scss";
@import "../../sass/mixins/_box-model.scss";
@import "../../sass/mixins/_breakpoint.scss";
@import "../../sass/mixins/_transforms.scss";

.ticket-type-add-to-basket {
    &.sold-out {
        opacity: 0.65;
        &.enquiry-enabled {
            opacity: 1;
        }
        .book-tickets-button {
            cursor: not-allowed;
        }
    }
    .quantity-down,
    .quantity-up {
        user-select: none;
        cursor: pointer;
        opacity: 0.1;
        font-size: 1.25rem;
        &.enabled {
            opacity: 0.5;
            &:hover {
                opacity: 1;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// resources/js/components/ticket-type-add-to-basket.vue